import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext'; // Add this import
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { createJobPost } from '@/lib/jobHelpers';

const SuccessToast = ({ onComplete }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.3 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.3 }}
    className="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
  >
    <motion.div
      initial={{ y: 50 }}
      animate={{ y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2 }}
        className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4"
      >
        <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
      </motion.div>
      <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
        Success!
      </h3>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        Your job posting has been created successfully.
      </p>
      <motion.div
        className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden"
        initial={{ width: "0%" }}
        animate={{ width: "100%" }}
        transition={{ duration: 2 }}
        onAnimationComplete={onComplete}
      />
    </motion.div>
  </motion.div>
);

const CreateJob = () => {
  const [showToast, setShowToast] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth(); // Add this line to get current user

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData(e.target);
      const jobData = {
        title: formData.get('title'),
        company: formData.get('company'),
        location: formData.get('location'),
        description: formData.get('description'),
        applyLink: formData.get('applyLink'),
      };

      // Validate required fields
      if (!jobData.title || !jobData.company || !jobData.description) {
        alert('Please fill in all required fields');
        return;
      }

      if (!user || !user.uid) {
        alert('You must be logged in to post a job');
        return;
      }

      const result = await createJobPost(jobData, user.uid, user.name);
      
      if (result.success) {
        setShowToast(true);
      } else {
        alert(result.error || 'Failed to create job post');
      }
    } catch (error) {
      console.error('Error in job creation:', error);
      alert('An unexpected error occurred. Please try again.');
    }
  };

  const handleToastComplete = () => {
    setShowToast(false);
    navigate('/jobs'); // Changed from '/' to '/jobs' to redirect to job listings
  };

  return (
    <>
      <AnimatePresence>
        {showToast && <SuccessToast onComplete={handleToastComplete} />}
      </AnimatePresence>

      <div className="w-full max-w-2xl mx-auto p-4 sm:p-6 lg:p-8">
        <Card className="dark:bg-gray-800">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl md:text-3xl font-bold dark:text-white">
              Post a New Job
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">
                  Job Title
                </label>
                <Input 
                  name="title" 
                  required 
                  placeholder="e.g. Software Engineer"
                  className="dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">
                  Company
                </label>
                <Input 
                  name="company" 
                  required 
                  placeholder="Your company name"
                  className="dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">
                  Location
                </label>
                <Input 
                  name="location" 
                  required 
                  placeholder="e.g. San Francisco, CA"
                  className="dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">
                  Application Link
                </label>
                <Input 
                  name="applyLink" 
                  type="url" 
                  required 
                  placeholder="https://..."
                  className="dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">
                  Job Description
                </label>
                <Textarea 
                  name="description"
                  required
                  placeholder="Describe the job role, requirements, and any other relevant details..."
                  className="min-h-[200px] dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-400"
                />
              </div>
              
              <Button type="submit" className="w-full md:w-auto md:min-w-[200px] dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white">
                Post Job
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default CreateJob;
